import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Play,
  Library,
  FolderOpen,
  Calendar,
  ChevronRight,
} from 'lucide-react';

interface RenderedVideo {
  id: string;
  name: string;
  thumbnailUrl?: string;
  url?: string;
  savedS3Url?: string;
  timestamp: string;
  duration: number;
  status: string;
  renderId?: string;
}

interface VideoProjectsPanelProps {
  onSelectVideo: (video: { url: string; thumbnailUrl?: string; name: string; renderId?: string }) => void;
  onProjectChange?: (project: string) => void;
  currentProject?: string;
}

export const VideoProjectsPanel: React.FC<VideoProjectsPanelProps> = ({
  onSelectVideo,
  onProjectChange,
  currentProject: externalProject,
}) => {
  const [projects, setProjects] = useState<string[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [renderedVideos, setRenderedVideos] = useState<RenderedVideo[]>([]);
  const [expandedProject, setExpandedProject] = useState<string | null>(null);

  const [dbProjects, setDbProjects] = useState<any[]>([]);

  // Load projects - try database first, fall back to localStorage
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const response = await fetch('/api/projects', { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          if (data.projects && data.projects.length > 0) {
            setDbProjects(data.projects);
            setProjects(data.projects.map((p: any) => p.name));
            const currentProject = localStorage.getItem('currentProject_NLE') || data.projects[0].name;
            setSelectedProject(currentProject);
            setExpandedProject(currentProject);
            return;
          }
        }
      } catch (e) {
        // API not available, fall back to localStorage
      }

      // Fallback to localStorage
      try {
        const saved = localStorage.getItem('projects_NLE');
        const projectList = saved ? JSON.parse(saved) : ['default'];
        setProjects(projectList);
        const currentProject = localStorage.getItem('currentProject_NLE') || 'default';
        setSelectedProject(currentProject);
        setExpandedProject(currentProject);
      } catch (e) {
        setProjects(['default']);
        setSelectedProject('default');
      }
    };

    loadProjects();
  }, []);

  // Load saved library videos when project changes - try database first
  useEffect(() => {
    if (!selectedProject) return;

    // Try database first
    const dbProject = dbProjects.find(p => p.name === selectedProject);
    if (dbProject && dbProject.savedRenders) {
      const validVideos = dbProject.savedRenders.filter((v: RenderedVideo) =>
        v.savedS3Url || v.url
      ).map((v: RenderedVideo) => ({
        ...v,
        url: v.savedS3Url || v.url,
      }));
      setRenderedVideos(validVideos);
      return;
    }

    // Fallback to localStorage
    try {
      const saved = localStorage.getItem(`project_${selectedProject}_savedRenders`);
      const videos = saved ? JSON.parse(saved) : [];
      const validVideos = videos.filter((v: RenderedVideo) =>
        v.savedS3Url || v.url
      ).map((v: RenderedVideo) => ({
        ...v,
        url: v.savedS3Url || v.url,
      }));
      setRenderedVideos(validVideos);
    } catch (e) {
      setRenderedVideos([]);
    }
  }, [selectedProject, dbProjects]);

  const handleProjectChange = (project: string) => {
    setSelectedProject(project);
    setExpandedProject(project);
    // Sync with localStorage and notify parent
    localStorage.setItem('currentProject_NLE', project);
    if (onProjectChange) {
      onProjectChange(project);
    }
  };

  // Sync with external project prop if provided
  useEffect(() => {
    if (externalProject && externalProject !== selectedProject) {
      setSelectedProject(externalProject);
      setExpandedProject(externalProject);
    }
  }, [externalProject]);

  const handleSelectVideo = (video: RenderedVideo) => {
    if (video.url) {
      onSelectVideo({
        url: video.url,
        thumbnailUrl: video.thumbnailUrl,
        name: video.name,
        renderId: video.renderId,
      });
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Library className="h-4 w-4" />
          Saved Library
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Project Selector */}
        <div className="flex items-center gap-2">
          <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <Select value={selectedProject} onValueChange={handleProjectChange}>
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Select project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((project) => (
                <SelectItem key={project} value={project}>
                  {project}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Videos List */}
        <ScrollArea className="h-[300px]">
          {renderedVideos.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <Library className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                No saved videos
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Save renders to Library in Video Enhancer
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {renderedVideos.map((video) => (
                <div
                  key={video.id}
                  className="group flex items-center gap-2 p-2 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => handleSelectVideo(video)}
                >
                  {/* Thumbnail */}
                  <div className="w-16 h-10 bg-muted rounded overflow-hidden flex-shrink-0 relative">
                    {video.thumbnailUrl ? (
                      <img
                        src={video.thumbnailUrl}
                        alt={video.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Play className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                    {/* Duration badge */}
                    <div className="absolute bottom-0.5 right-0.5 bg-black/70 px-1 py-0.5 rounded text-[10px] text-white">
                      {Math.round(video.duration)}s
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{video.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(video.timestamp).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Schedule button */}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectVideo(video);
                    }}
                  >
                    <Calendar className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {renderedVideos.length > 0 && (
          <p className="text-xs text-muted-foreground text-center">
            Click a video to schedule it
          </p>
        )}
      </CardContent>
    </Card>
  );
};
